import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { PLATFORMS } from '@utils/types/connections/platforms.ts';
import type { PlatformId } from '@utils/types/connections/connection.ts';
import PlatformIcon from '../platform-icon';

interface Props {
  value: PlatformId | null;
  onChange: (platformId: PlatformId) => void;
  /**
   * Platform ids Scriptix allows this organization. ``undefined`` while
   * unknown: every platform is shown and the server still refuses a
   * blocked one.
   */
  allowedPlatforms?: readonly string[];
}

export default function StepChoosePlatform({
  value,
  onChange,
  allowedPlatforms
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const platforms = allowedPlatforms
    ? PLATFORMS.filter(platform => allowedPlatforms.includes(platform.id))
    : PLATFORMS;

  if (platforms.length === 0) {
    return (
      <p className="text-sm" style={{ color: getColour('inactiveText') }}>
        {t('publishing-wizard-no-platforms')}
      </p>
    );
  }

  return (
    <fieldset
      className="flex flex-col gap-3"
      aria-labelledby="publishing-wizard-platform-legend"
    >
      <legend id="publishing-wizard-platform-legend" className="sr-only">
        {t('publishing-wizard-step-platform')}
      </legend>
      <p className="text-sm" style={{ color: getColour('inactiveText') }}>
        {t('publishing-wizard-platform-help')}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {platforms.map(platform => {
          const selected = value === platform.id;
          const disabled = !!platform.comingSoon;
          return (
            <label
              key={platform.id}
              aria-disabled={disabled ? true : undefined}
              className={`relative flex flex-col gap-2 rounded-xl border p-4 outline-none focus-within:ring-2 focus-within:ring-offset-2 ${
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
              }`}
              style={{
                borderColor:
                  selected && !disabled
                    ? getColour('activeText')
                    : getColour('hairline'),
                background: getColour('secondLayer'),
                boxShadow:
                  selected && !disabled
                    ? `0 0 0 2px ${getColour('activeText')}22`
                    : undefined
              }}
            >
              <input
                type="radio"
                name="publishing-platform"
                value={platform.id}
                checked={selected && !disabled}
                disabled={disabled}
                onChange={() => !disabled && onChange(platform.id)}
                className="sr-only"
                aria-labelledby={`platform-${platform.id}-name`}
                aria-describedby={`platform-${platform.id}-tagline`}
              />
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                  style={{ background: getColour('thirdLayer') }}
                >
                  <PlatformIcon platform={platform.id} className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    id={`platform-${platform.id}-name`}
                    className="flex items-center gap-2 text-sm font-semibold"
                  >
                    {platform.displayName}
                    {disabled && (
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                        style={{
                          background: getColour('thirdLayer'),
                          color: getColour('inactiveText')
                        }}
                      >
                        {t('publishing-platform-coming-soon')}
                      </span>
                    )}
                  </p>
                  <p
                    id={`platform-${platform.id}-tagline`}
                    className="text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {disabled
                      ? t('publishing-platform-coming-soon-help', {
                          platform: platform.displayName
                        })
                      : t(platform.taglineKey)}
                  </p>
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
